import { colocService } from './coloc.service';
import { roomsService } from './rooms.service';
import { photosService } from './photos.service';
import { authService } from './auth.service';
import type { Coloc } from '../types/coloc';
import type { RoomDetail } from '../types/room';

/**
 * Onboarding state — déduit de l'état réel (pas de flag backend).
 * L'onboarding est "terminé" quand l'owner a une coloc ET au moins une chambre
 * ayant au moins une photo (cf. saas/08-conception-onboarding-owner.md).
 */
export type OnboardingStep = 'coloc' | 'room' | 'photo' | 'done';

export interface OnboardingState {
  done: boolean;
  step: OnboardingStep;
  coloc: Coloc | null;
  room: RoomDetail | null; // la première chambre, si elle existe
}

/**
 * Clé de « wizard passé », par utilisateur : sans l'identifiant, un compte qui
 * passe l'onboarding le passerait aussi pour le suivant sur le même poste.
 */
function skipKey(): string | null {
  const id = authService.getCurrentUser()?.user?.id;
  return id ? `onboarding:skipped:${id}` : null;
}

export const onboardingService = {
  /** Marque le wizard comme passé. Stocké côté navigateur seulement : le
   *  wizard réapparaîtra sur un autre poste, avec son bouton « Passer » —
   *  conséquence assez bénigne pour ne pas justifier une colonne en base. */
  skip(): void {
    const cle = skipKey();
    if (cle) localStorage.setItem(cle, '1');
  },

  isSkipped(): boolean {
    const cle = skipKey();
    return cle ? localStorage.getItem(cle) === '1' : false;
  },

  /**
   * Détermine où en est l'owner. Court-circuite dès qu'une étape manque
   * (évite des appels inutiles).
   */
  async getState(): Promise<OnboardingState> {
    const colocs = await colocService.getColocs();
    if (colocs.length === 0) {
      return { done: false, step: 'coloc', coloc: null, room: null };
    }
    const coloc = colocs[0];

    const rooms = await roomsService.listForColoc(coloc.id);
    if (rooms.length === 0) {
      return { done: false, step: 'room', coloc, room: null };
    }

    // Au moins une chambre doit avoir au moins une photo.
    for (const room of rooms) {
      const photos = await photosService.list('room', room.id);
      if (photos.length > 0) {
        return { done: true, step: 'done', coloc, room };
      }
    }
    // Des chambres existent mais aucune n'a de photo → étape photo (sur la 1ère).
    return { done: false, step: 'photo', coloc, room: rooms[0] };
  },
};
